/**
 * Sign in, sync status, sign out, delete.
 *
 * An account is optional here and the copy says so. The app works fully
 * without one — progress lives in the browser, and Backup already moves it
 * between devices by hand. Signing in buys doing that automatically, and
 * nothing else, so the dialog does not pretend otherwise.
 */

import { useEffect, useRef, useState } from "react";
import { deleteAccount, fetchAccount, requestSignIn, signOut } from "@/lib/sync/api.ts";
import { fetchSitekey, loadTurnstile } from "@/lib/sync/turnstile.ts";
import { useAccountStore } from "@/state/account.ts";
import styles from "./AccountDialog.module.css";

type Stage = "form" | "sent" | "working";

export function AccountDialog() {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>("form");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState("");
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const widgetRef = useRef<HTMLDivElement | null>(null);
  // The solved token, or null. Held in a ref rather than state because it is
  // read at submit time and changing it must not re-render the form — a
  // re-render would remount the widget and discard the solve.
  const tokenRef = useRef<string | null>(null);
  const [sitekey, setSitekey] = useState<string | null>(null);

  const account = useAccountStore((s) => s.email);
  const status = useAccountStore((s) => s.status);
  const message = useAccountStore((s) => s.message);
  const setAccount = useAccountStore((s) => s.setEmail);
  const sync = useAccountStore((s) => s.sync);

  // Ask the server who we are, once. The session is an HttpOnly cookie, so
  // this is the only way to know — and a redirect back from a sign-in link
  // lands here with ?signin=ok, which is the moment to check.
  useEffect(() => {
    let live = true;
    void fetchAccount().then((found) => {
      if (!live || !found) return;
      setAccount(found);
      void sync();
    });
    return () => {
      live = false;
    };
  }, [setAccount, sync]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Ask what this deployment wants only once someone opens the dialog. Most
  // visitors never do, and this is the request that reveals a visit to a third
  // party — see the note in lib/sync/turnstile.ts.
  useEffect(() => {
    if (!open || account || sitekey !== null) return;
    let live = true;
    void fetchSitekey().then((key) => {
      if (live && key) setSitekey(key);
    });
    return () => {
      live = false;
    };
  }, [open, account, sitekey]);

  // Render the widget once the form is actually on screen and we have a key.
  useEffect(() => {
    if (!open || account || stage !== "form" || !sitekey) return;
    const host = widgetRef.current;
    if (!host) return;

    let id: string | undefined;
    let live = true;
    void loadTurnstile().then((api) => {
      if (!live || !api || !widgetRef.current) return;
      id = api.render(widgetRef.current, {
        sitekey,
        theme: "auto",
        callback: (token) => {
          tokenRef.current = token;
        },
        // A token is single-use and expires. Clearing on both means the form
        // asks again rather than submitting something the server will reject.
        "expired-callback": () => {
          tokenRef.current = null;
        },
        "error-callback": () => {
          tokenRef.current = null;
        },
      });
    });

    return () => {
      live = false;
      if (id && window.turnstile) window.turnstile.remove(id);
    };
  }, [open, account, stage, sitekey]);

  const close = () => {
    setOpen(false);
    setStage("form");
    setError(null);
    setConfirmDelete("");
  };

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    setStage("working");
    setError(null);
    try {
      await requestSignIn(email, tokenRef.current ?? undefined);
      setStage("sent");
    } catch (err) {
      // The token is spent whether or not the request succeeded, so a retry
      // needs a fresh one.
      tokenRef.current = null;
      if (window.turnstile) window.turnstile.reset();
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setStage("form");
    }
  };

  return (
    <>
      <button type="button" className={styles.trigger} onClick={() => setOpen(true)}>
        {account ? "Account" : "Sign in"}
      </button>

      <dialog ref={dialogRef} className={styles.dialog} onClose={close}>
        <div className={styles.head}>
          <h2 className={styles.title}>{account ? "Your account" : "Sign in"}</h2>
          <button type="button" className={styles.close} onClick={close}>
            Done
          </button>
        </div>

        {account ? (
          <>
            <p className={styles.note}>
              Signed in as <strong>{account}</strong>. Your watched list and ratings sync to any
              device you sign in on.
            </p>
            <p className={styles.status} data-status={status}>
              {status === "syncing" && "Syncing…"}
              {status === "synced" && (message ?? "Everything's up to date.")}
              {status === "offline" && message}
              {status === "error" && message}
              {status === "idle" && "Not synced yet."}
            </p>
            <div className={styles.actions}>
              <button type="button" className={styles.primary} onClick={() => void sync()}>
                Sync now
              </button>
              <button
                type="button"
                className={styles.quiet}
                onClick={async () => {
                  await signOut();
                  setAccount(null);
                  close();
                }}
              >
                Sign out
              </button>
            </div>

            <section className={styles.danger}>
              <h3 className={styles.dangerTitle}>Delete account</h3>
              <p className={styles.note}>
                Deletes your account, your synced data and every session, immediately and for good.
                What's in this browser stays until you clear it. Export first if you want a copy:
                the Backup button gives you everything.
              </p>
              <label className={styles.confirmLabel}>
                Type <strong>{account}</strong> to confirm
                <input
                  className={styles.input}
                  value={confirmDelete}
                  onChange={(e) => setConfirmDelete(e.target.value)}
                  autoComplete="off"
                />
              </label>
              <button
                type="button"
                className={styles.destructive}
                disabled={confirmDelete.trim().toLowerCase() !== account}
                onClick={async () => {
                  try {
                    await deleteAccount(confirmDelete);
                    setAccount(null);
                    close();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : "Couldn't delete the account.");
                  }
                }}
              >
                Delete my account
              </button>
            </section>
          </>
        ) : stage === "sent" ? (
          <>
            {/*
             * Deliberately says nothing about whether an account exists. The
             * server refuses to distinguish, and claiming "we've sent you a
             * link" when we may not have would be a lie — "if there's an
             * account" is the honest hedge and costs the reader nothing.
             */}
            <p className={styles.note}>
              Check <strong>{email}</strong>. If that address can be signed in, a link is on its
              way. It works once and expires in fifteen minutes.
            </p>
            <button type="button" className={styles.quiet} onClick={() => setStage("form")}>
              Use a different address
            </button>
          </>
        ) : (
          <form onSubmit={send}>
            <p className={styles.note}>
              An account is optional. Everything works without one. This only adds syncing your
              watched list and ratings between devices.
            </p>
            <p className={styles.note}>
              No password. Enter your email and we'll send a link that signs you in.
            </p>
            <label className={styles.confirmLabel}>
              Email
              <input
                className={styles.input}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </label>
            <div ref={widgetRef} className={styles.challenge} />
            {/*
             * Not disabled while the challenge is outstanding. A widget that
             * fails to load — blocked by an extension, or offline — would
             * otherwise leave a button that can never be pressed and no
             * explanation. Submitting without a token gets a message that
             * says what went wrong instead.
             */}
            <button type="submit" className={styles.primary} disabled={stage === "working"}>
              {stage === "working" ? "Sending…" : "Email me a link"}
            </button>
          </form>
        )}

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </dialog>
    </>
  );
}
